import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { LogIn, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/PageHeader";
import { NewBusinessDialog } from "@/pages/super/NewBusinessDialog";
import { api, errMsg } from "@/lib/api";

const STATUS = { active: "bg-emerald-600", free: "bg-amber-500", pending_payment: "bg-slate-400", cancelled: "bg-rose-600", past_due: "bg-rose-500", trial: "bg-indigo-500" };

export default function SuperBusinesses() {
  const [rows, setRows] = useState([]);
  const [plans, setPlans] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/super/businesses").then((r) => setRows(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    api.get("/super/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, [load]);

  const update = async (id, body) => {
    try {
      await api.put(`/super/businesses/${id}`, body);
      toast.success("Business updated");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const loginAs = async (id) => {
    try {
      await api.post(`/super/impersonate/${id}`);
      window.location.assign("/app");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const filtered = rows.filter((b) => `${b.name} ${b.owner?.email}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader title="Businesses" subtitle={`${rows.length} accounts`}>
        <Input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="w-56" data-testid="business-search-input" />
        <Button onClick={() => setOpen(true)} className="rounded-full" data-testid="new-business-btn"><Plus className="mr-1 h-4 w-4" />New business</Button>
      </PageHeader>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Business</TableHead><TableHead>Plan</TableHead><TableHead>Status</TableHead>
              <TableHead>Staff</TableHead><TableHead>Free</TableHead><TableHead>Suspended</TableHead><TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((b) => (
              <TableRow key={b.id} data-testid={`business-row-${b.id}`}>
                <TableCell>
                  <p className="font-semibold">{b.name}</p>
                  <p className="text-xs text-slate-500">{b.owner?.email}</p>
                </TableCell>
                <TableCell>
                  <Select value={b.plan_id || ""} onValueChange={(v) => update(b.id, { plan_id: v })}>
                    <SelectTrigger className="w-36" data-testid={`business-plan-select-${b.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
                <TableCell><Badge className={`${STATUS[b.plan_status] || "bg-slate-400"} text-white`} data-testid={`business-status-${b.id}`}>{b.plan_status}</Badge></TableCell>
                <TableCell>{b.staff_count}</TableCell>
                <TableCell>
                  <Switch checked={!!b.free_account} onCheckedChange={(v) => update(b.id, v ? { free_account: true } : { free_account: false, plan_status: "pending_payment" })} data-testid={`business-free-switch-${b.id}`} />
                </TableCell>
                <TableCell>
                  <Switch checked={!!b.suspended} onCheckedChange={(v) => update(b.id, { suspended: v })} data-testid={`business-suspend-switch-${b.id}`} />
                </TableCell>
                <TableCell>
                  <Button size="sm" variant="outline" onClick={() => loginAs(b.id)} data-testid={`login-as-btn-${b.id}`}><LogIn className="mr-1 h-3.5 w-3.5" />Login as</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <NewBusinessDialog open={open} onOpenChange={setOpen} plans={plans} onDone={load} />
    </div>
  );
}
